import callApi from "../../utils/api";
import {
  API_BASE_URL,
  AI_PLANET_PROXY,
  CLOUDFRONT_URL,
} from "../../utils/constants";
import { parseParams } from "../../utils";

const api = callApi(API_BASE_URL || "");
const aiPlanetApi = callApi(AI_PLANET_PROXY || "");
const pageSize = 8;

const imageUploadParams = new URLSearchParams({
  fileCategory: "MASTER_SERVICE",
  subFolders: "COURSES",
  fileType: "templateUrl",
});

const courseFileTypeMap = {
  thumbnailUrl: "THUMBNAIL_URL",
  imageUrl: "IMAGE_URL",
  templateURL: "CERTIFICATE",
};

const assessmentJsonUploadParams = new URLSearchParams({
  fileCategory: "IR_SERVICE",
  // subFolders: 'IR_GRADE',
  fileType: "assessment",
});

export const CREATE_COURSE = "/v1/api/master/courses";
export const GET_COURSE_ALL = "/v1/api/master/courses?{{query}}";
export const GET_COURSE_BY_ID = "/v1/api/master/courses/{{id}}";
export const GET_COURSE_LIST = "/v1/api/master/courses/list?{{query}}";
export const UPDATE_COURSE_BY_ID = `/v1/api/master/courses/{{id}}`;
// export const UPLOAD_IMAGE = '/v1/api/master/courses/upload/{{id}}?{{query}}';
export const UPLOAD_IMAGE =
  "/v1/api/file/upload-custom?path=IR-SERVICE/{{type}}";
export const DELETE_COURSE_BY_ID = "/v1/api/master/courses/{{id}}";
export const UPDATE_PREREQUISITES =
  "/v1/api/master/courses/prerequisites/{{id}}";

// IR level -> course mapping (from ir-level-course-mapping.json, enriched with
// courseName/studentType by the backend). Used to group courses under their level.
export const GET_LEVEL_COURSE_MAPPING = "/v1/api/master/courses/level-mapping";

export const GET_LESSON_PLAN = "/v1/api/master/lessonPlan/{{courseId}}";
export const CREATE_LESSON_PLAN = "/v1/api/master/lessonPlan/{{courseId}}";
export const DELETE_LESSON_PLAN =
  "/v1/api/master/lessonPlan/{{id}}/{{courseId}}";
export const UPDATE_LESSON_PLAN = "/v1/api/master/lessonPlan/{{lessonPlanId}}";

export const GET_ASSESSMENT_BY_ID = "/v1/api/master/assessments/{{id}}";
export const GET_QUESTIONS_BY_ASSESSMENT_ID = `${CLOUDFRONT_URL}/assessments/{{assessmentId}}.json`;
export const PUBLISH_QUESTION_TO_ASSESSMENT =
  "/v1/api/master/assessments/publish/{{id}}";

export const UPLOAD_ASSESSMENT_BY_ID =
  "/v1/api/file/upload-custom/ir?path=IR-SERVICE/assessments";

export const CREATE_ASSESSMENT = "/v1/api/master/assessments";
export const DELETE_ASSESSMENT = "/v1/api/master/assessments/{{id}}";
export const PUBLISH_ASSESSMENT_CHAPTER_TO_LESSON_PLAN =
  "/v1/api/master/assessments/lesson-plan/chapterId/{{id}}";
export const PUBLISH_ASSESSMENT_SECTION_TO_LESSON_PLAN =
  "/v1/api/master/assessments/lesson-plan/sectionId/{{id}}";
export const PUBLISH_ASSESSMENT_TO_COURSE =
  "/v1/api/master/assessments/lesson-plan/courseId/{{id}}";
export const UPDATE_ASSESSMENT_BY_ID = `/v1/api/master/assessments/{{id}}`;

export const GENERATE_QUESTION = "/api/proxy/generate-diagnostic-test";

export const GET_NEXT_COURSE =
  "/v1/api/user/students/next-ir-level/{{studentId}}?courseId={{courseId}}&level={{level}}";

// First course at an IR level for a student, filtered by their
// geographical_type. Used by the diagnostic-completion flow where there is
// no "current courseId" to feed into next-ir-level.
export const GET_FIRST_COURSE_AT_LEVEL =
  "/v1/api/user/students/level-course/{{studentId}}?level={{level}}";

export const getAll = async (payload) => {
  let query = {};
  if (payload && payload.payload) {
    query = { ...payload.payload, ...query };
  }

  if (!query.pageSize || query.pageSize < 1) {
    delete query.pageSize;
  }

  let params = new URLSearchParams(query).toString();

  return await api.get(parseParams(GET_COURSE_ALL, { query: params }));
};

export const getAllWithoutPage = async (payload) => {
  let query = {};
  if (payload && payload.payload) {
    query = { ...payload.payload, ...query };
  }

  let params = new URLSearchParams(query).toString();

  return await api.get(parseParams(GET_COURSE_ALL, { query: params }));
};

export const getCourseList = async (payload) => {
  const query = new URLSearchParams(payload).toString();
  return await api.get(parseParams(GET_COURSE_LIST, { query }));
};

export const getById = async (payload) =>
  api.get(parseParams(GET_COURSE_BY_ID, payload));

export const getLevelCourseMapping = async () =>
  api.get(GET_LEVEL_COURSE_MAPPING);

export const updateById = async (payload) =>
  api.put(parseParams(UPDATE_COURSE_BY_ID, { id: payload.id }), payload);

export const create = async (payload) => api.post(CREATE_COURSE, payload);

export const uploadImage = async (payload) => {
  if (payload.field && courseFileTypeMap[payload.field]) {
    imageUploadParams.set("fileType", courseFileTypeMap[payload.field]);
  }

  return api.post(
    parseParams(UPLOAD_IMAGE, {
      id: payload.id,
      query: imageUploadParams.toString(),
    }),
    payload.formData,
    {},
    true,
  );
};
export const deleteById = async (payload) =>
  await api.delete(parseParams(DELETE_COURSE_BY_ID, payload));
export const updatePrequisites = async (payload) =>
  await api.put(
    parseParams(UPDATE_PREREQUISITES, { id: payload.id }),
    payload.body,
  );

export const getNextCourse = async (payload) => {
  return await api.get(parseParams(GET_NEXT_COURSE, payload));
};

export const getFirstCourseAtLevel = async (payload) => {
  return await api.get(parseParams(GET_FIRST_COURSE_AT_LEVEL, payload));
};

// Lesson plan API handlers
export const getLessonPlan = async (payload) =>
  await api.get(parseParams(GET_LESSON_PLAN, { courseId: payload.id }));
export const createLessonPlan = async (payload) =>
  await api.post(parseParams(CREATE_LESSON_PLAN, payload.params), payload.body);
export const deleteLessonPlan = async (payload) =>
  await api.delete(parseParams(DELETE_LESSON_PLAN, payload));
export const updateLessonPlan = async (payload) =>
  api.put(
    parseParams(UPDATE_LESSON_PLAN, { lessonPlanId: payload.lessonPlanId }),
    payload.body,
  );

// Assessment API handlers
export const getAssessmentById = async (payload) =>
  await api.get(parseParams(GET_ASSESSMENT_BY_ID, payload));
export const getQuestionsByAssessmentId = async (payload) => {
  if (typeof payload.id === "undefined" || payload.id.length == 0) return;

  // CloudFront answers a missing snapshot with a 403 XML body, which
  // response.json() would choke on. Fail with the URL instead.
  const url = parseParams(GET_QUESTIONS_BY_ASSESSMENT_ID, { assessmentId: payload.id });
  const response = await api.asyncFetchContent(url, null);
  if (!response.ok) {
    throw new Error(`Assessment questions not found (${response.status}): ${url}`);
  }
  return await response.json();
};
export const deleteAssessment = async (payload) =>
  await api.delete(parseParams(DELETE_ASSESSMENT, payload));
export const createAssessment = async (payload) =>
  api.post(CREATE_ASSESSMENT, payload);
export const updateAssessmentById = async (payload) =>
  api.put(parseParams(UPDATE_ASSESSMENT_BY_ID, { id: payload.id }), payload);
export const uploadAssessmentById = async (payload) => {
  assessmentJsonUploadParams.set("fileType", "assessment");
  return api.post(
    parseParams(UPLOAD_ASSESSMENT_BY_ID, {
      id: payload.id,
      query: assessmentJsonUploadParams.toString(),
    }),
    payload.formData,
    {},
    true,
  );
};
export const publishQuestionToAssessment = async (payload) =>
  api.post(
    parseParams(PUBLISH_QUESTION_TO_ASSESSMENT, { id: payload.id }),
    payload,
  );
export const publishAssessmentChapterToLessonPlan = async (payload) =>
  api.put(
    parseParams(PUBLISH_ASSESSMENT_CHAPTER_TO_LESSON_PLAN, { id: payload.id }),
    payload,
  );
export const publishAssessmentSectionToLessonPlan = async (payload) =>
  api.put(
    parseParams(PUBLISH_ASSESSMENT_SECTION_TO_LESSON_PLAN, { id: payload.id }),
    payload,
  );
export const publishAssessmentToCourse = async (payload) =>
  api.put(
    parseParams(PUBLISH_ASSESSMENT_TO_COURSE, { id: payload.id }),
    payload,
  );

export const generateQuestion = async (payload) =>
  await aiPlanetApi.post(GENERATE_QUESTION, payload);
